"use client";

import * as stylex from "@stylexjs/stylex";
import { forwardRef, useState } from "react";
import { useFocus, useHover, usePress } from "react-aria";

import {
  stylesOpacity,
  stylesOutline,
  stylesScale,
} from "../../../../../../global/stylex/styles";
import {
  radius,
  semanticColors,
  spacing,
  stroke,
} from "../../../../../../global/stylex/vars.stylex";
import type {
  HallowElement,
  HallowElementProps,
  KeyedElement,
  WithAsChild,
} from "../../../../../types";
import {
  deepMergeStyleXProps,
  determineChildrenInject,
  determineElementFromAsChild,
} from "../../../../../utils";
import { Text } from "../../../_core";

const hallowElement: HallowElement = "div";

const styles = stylex.create({
  determinedElement: {
    alignItems: "center",
    display: "flex",
    gap: spacing.m,
    paddingRight: spacing.m,
    width: spacing.full,
  },
  illustrationImg: (props: { color: string }) => ({
    "-webkit-user-drag": "none",
    borderBottomLeftRadius: spacing.s,
    borderColor: props.color,
    borderRightStyle: "solid",
    borderTopRightRadius: spacing.s,
    borderTopStyle: "solid",
    borderWidth: stroke.regular,
    bottom: spacing.none,
    boxSizing: "border-box",
    height: spacing.xl,
    left: spacing.none,
    objectFit: "cover",
    objectPosition: "center",
    objectRepeat: "no-repeat",
    position: "absolute",
    userSelect: "none",
    width: spacing.xl,
  }),
  img: {
    "-webkit-user-drag": "none",
    borderRadius: radius.s,
    height: 80,
    objectFit: "cover",
    objectPosition: "center",
    objectRepeat: "no-repeat",
    userSelect: "none",
    width: 80,
  },
  imgContainer: {
    height: 80,
    position: "relative",
  },
  infoContainer: {
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    gap: spacing.xxs,
    minWidth: spacing.none,
  },
  infoTopContainer: {
    display: "flex",
    flexDirection: "column",
    gap: spacing.t,
  },
  metadata: {
    color: semanticColors.neutralsMedium,
  },
  rightActionsContainer: {
    alignItems: "center",
    display: "flex",
    gap: spacing.m,
  },
});

export type SessionSmallProps = WithAsChild<
  HallowElementProps<typeof hallowElement>
> & {
  activeState?: boolean;
  color: string;
  description: string;
  focusState?: boolean;
  hoverState?: boolean;
  illustrationImgSrc: string;
  imgSrc: string;
  metadata: string;
  rightActions?: KeyedElement | KeyedElement[];
  title: string;
};

export const SessionSmall = forwardRef<any, SessionSmallProps>(
  (
    {
      activeState = true,
      asChild = false,
      children,
      color,
      description,
      focusState = true,
      hoverState = true,
      illustrationImgSrc,
      imgSrc,
      metadata,
      rightActions,
      title,
      ...props
    },
    ref,
  ) => {
    const [focus, setFocus] = useState(false);

    const { isPressed: active, pressProps: activeProps } = usePress({});
    const { focusProps } = useFocus({
      onFocusChange: (focus) => setFocus(focus),
    });
    const { isHovered: hover, hoverProps } = useHover({});

    const DeterminedElement = determineElementFromAsChild({
      asChild,
      hallowElement,
    });

    const determinedChildren = determineChildrenInject({
      afterChildrenInject: [
        <div key="imgContainer" {...stylex.props(styles.imgContainer)}>
          <img
            alt={title}
            src={imgSrc}
            {...stylex.props(
              styles.img,
              stylesOutline.base,
              focus && focusState ? stylesOutline.focusControlled : null,
              hover && hoverState ? stylesOutline.hoverControlled : null,
            )}
          />
          <img
            alt={title}
            src={illustrationImgSrc}
            {...stylex.props(styles.illustrationImg({ color }))}
          />
        </div>,
        <div key="infoContainer" {...stylex.props(styles.infoContainer)}>
          <div {...stylex.props(styles.infoTopContainer)}>
            <Text overflow="ellipsis" size="l" type="title">
              {title}
            </Text>
            <Text overflow="ellipsis" size="s" type="body">
              {description}
            </Text>
          </div>
          <Text
            overflow="ellipsis"
            size="l"
            styleXArray={[styles.metadata]}
            type="detail"
          >
            {metadata}
          </Text>
        </div>,
        rightActions ? (
          <div
            key="rightActions"
            {...stylex.props(styles.rightActionsContainer)}
          >
            {rightActions}
          </div>
        ) : null,
      ],
      children: children as JSX.Element,
    });

    return (
      <DeterminedElement
        ref={ref}
        {...activeProps}
        {...focusProps}
        {...hoverProps}
        {...deepMergeStyleXProps({
          object1: stylex.props(
            styles.determinedElement,
            activeState ? stylesOpacity.active : null,
            activeState ? stylesOpacity.base : null,
            activeState && hoverState ? stylesScale.base : null,
            activeState && hoverState ? stylesScale.l : null,
          ),
          object2: props,
        })}
      >
        {determinedChildren}
      </DeterminedElement>
    );
  },
);

SessionSmall.displayName = "SessionSmall";
